import { Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { GA_MEASUREMENT_ID } from "../../lib/analytics";
import {
  clearGoogleCookies,
  CONSENT_EVENT,
  readConsent,
  writeConsent,
  type Consent,
} from "../../lib/consent";
import { DICTS, useI18n } from "../../lib/i18n";

// Reads the stored choice and keeps it in sync with the rest of the app.
// Starts as `undefined` rather than `null` so the first render (which also runs
// during SSR) shows nothing at all: a banner that flashes in and then vanishes
// for someone who already chose is worse than one that appears a beat late.
function useConsent(): Consent | null | undefined {
  const [consent, setConsent] = useState<Consent | null | undefined>(undefined);
  useEffect(() => {
    const sync = () => setConsent(readConsent());
    sync();
    window.addEventListener(CONSENT_EVENT, sync);
    return () => window.removeEventListener(CONSENT_EVENT, sync);
  }, []);
  return consent;
}

/**
 * Injects gtag.js, and only ever after a yes.
 *
 * Nothing about Google Analytics reaches the document until this runs, so there
 * is no window in which a cookie is set and then apologised for.
 */
export function Analytics() {
  const consent = useConsent();

  // Everyone who visited before the banner existed is carrying `_ga` cookies we
  // never had permission for. Leaving them to expire two years from now would
  // mean processing without a lawful basis for two more years, so the absence
  // of a stored yes actively clears them. This also covers a visitor who
  // refuses: their old cookies go on the spot, not at the next visit.
  useEffect(() => {
    if (consent === "granted" || consent === undefined) return;
    clearGoogleCookies();
  }, [consent]);

  useEffect(() => {
    if (consent !== "granted" || !GA_MEASUREMENT_ID) return;
    if (document.getElementById("ga-src")) return;

    const w = window as unknown as { dataLayer?: unknown[]; gtag?: (...a: unknown[]) => void };
    w.dataLayer = w.dataLayer || [];
    w.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      w.dataLayer!.push(arguments);
    };
    w.gtag("js", new Date());

    // Our own visits, flagged so they do not pollute the numbers. Same key the
    // old inline script used, so devices already marked stay marked.
    let internal = false;
    try {
      const p = new URLSearchParams(location.search).get("internal");
      if (p === "1") localStorage.setItem("sunbedly_internal", "1");
      else if (p === "0") localStorage.removeItem("sunbedly_internal");
      internal = localStorage.getItem("sunbedly_internal") === "1";
    } catch {
      /* private mode */
    }

    w.gtag("config", GA_MEASUREMENT_ID, {
      ...(internal ? { traffic_type: "internal" } : {}),
      // Belt and braces on top of GA4's own behaviour: never pass the page's
      // query string or referrer wholesale, because a /manage/<token> URL in
      // either would put a working dashboard key into Google's logs.
      anonymize_ip: true,
    });

    const s = document.createElement("script");
    s.id = "ga-src";
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(s);
  }, [consent]);

  return null;
}

export function ConsentBanner() {
  const { lang, langFromUrl, suggestion, t } = useI18n();
  const consent = useConsent();
  const cardRef = useRef<HTMLDivElement>(null);

  // Consent is only valid if it is *informed*, and nobody is informed by a
  // sentence they cannot read. The page language follows the URL, so a Dutch
  // visitor landing on the English homepage would be asked in English. For this
  // dialog we use the language we think they actually speak, taken from the
  // country of the request or the browser, and fall back to the page.
  //
  // Only when the page has no locale of its own. Caught in dev: on
  // /nl/club/... with a request that Cloudflare placed in Italy, the dialog
  // came up in Italian over a Dutch page. Someone on /nl is there on purpose,
  // and a guess about their country does not get to overrule that.
  const askLang = langFromUrl ? lang : (suggestion ?? lang);
  const ask = (key: string) =>
    askLang === lang ? t(key) : (DICTS[askLang]?.[key] ?? DICTS.en[key] ?? key);

  // Move focus into the dialog so a keyboard or screen reader user meets the
  // choice instead of tabbing through the page behind it.
  useEffect(() => {
    if (consent === null) cardRef.current?.focus();
  }, [consent]);

  if (consent !== null) return null;

  // Both buttons are the same size and the same shape and sit next to each
  // other. Weighting them is what regulators strike down, and it would also
  // make the consent invalid, which would make the analytics data unusable.
  // A prominent dialog is allowed; a lopsided one is not.
  const button =
    "li-mono h-12 flex-1 rounded-lg border text-[14px] transition-colors active:translate-y-[1px]";

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-[#1C2430]/50 p-4 backdrop-blur-[2px] sm:items-center"
      // The backdrop deliberately does nothing when clicked: this dialog is
      // dismissed by choosing, not by tapping past it. That is not a cookie
      // wall, because refusing gives access to the entire site.
    >
      <div
        ref={cardRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ck-title"
        aria-describedby="ck-body"
        className="w-full max-w-lg overscroll-contain rounded-2xl border border-[#1C2430]/10 bg-[#F2EFE9] p-6 shadow-[0_30px_70px_-20px_rgba(10,20,35,0.6)] outline-none sm:p-7"
      >
        <p
          id="ck-title"
          className="li-display text-lg font-bold tracking-tight text-[#1C2430] sm:text-xl"
        >
          {ask("ck_title")}
        </p>
        <p id="ck-body" className="mt-2 text-[14px] leading-relaxed text-[#5B6472]">
          {ask("ck_body")}
        </p>
        <div className="mt-5 flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={() => writeConsent("denied")}
            className={`${button} border-[#1C2430]/30 text-[#1C2430] hover:border-[#1C2430]`}
          >
            {ask("ck_reject")}
          </button>
          <button
            type="button"
            onClick={() => writeConsent("granted")}
            className={`${button} border-[#1C2430] bg-[#1C2430] font-medium text-[#F2EFE9] hover:border-[#2C4FD6] hover:bg-[#2C4FD6]`}
          >
            {ask("ck_accept")}
          </button>
        </div>
        <Link
          to="/privacy"
          className="li-mono mt-4 inline-block text-[12px] text-[#5B6472] underline underline-offset-4 hover:text-[#2C4FD6]"
        >
          {ask("ck_more")}
        </Link>
      </div>
    </div>
  );
}
